import { cn, Pressable } from '@vidahealth/vida-ui';
import { cssInterop } from 'nativewind';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, View } from 'react-native';

import { useOnboardingSteps } from '@/modules/rulesEngine';

import { ChevronLeftIcon } from '../../icons';
import { FrostedSurface } from '../FrostedSurface';

// RN's `Animated.View` isn't auto-wired by nativewind, so a bare `className`
// prop is silently dropped. Without this, the progress fill renders with no
// background color (invisible bar). Same pattern as Autocomplete's wrapping
// of Reanimated's Animated.View.
cssInterop(Animated.View, { className: 'style' });

const ANIMATION_DURATION_MS = 350;

type OnboardingHeaderProps = {
  /** Max content width to align with the body column on wide viewports. */
  maxContentWidth: number;
  /**
   * Top padding for the safe-area inset (e.g. the device status bar on
   * native). Applied above the row so the back button and progress bar sit
   * below system chrome. Defaults to 0.
   */
  paddingTop?: number;
};

/**
 * Persistent header for the onboarding flow: back chevron + animated progress
 * bar, wrapped in an absolute-positioned FrostedSurface that overlays the
 * screen content so the content can scroll behind it.
 *
 * Step state (visibility, progress, back availability + action) is sourced
 * from `useOnboardingSteps` in `@/modules/rulesEngine` — a Sparta-aligned
 * hook that group-indexes progress and only allows back navigation inside
 * bi-directional groups. The chrome is purely presentational; the rules
 * engine owns "what does back mean here?" via `goBack`.
 *
 * Self-gates on `canShowHeader` — on steps that opt out (e.g.
 * /account-creation), the entire FrostedSurface chrome is skipped. The
 * onboarding layout still reserves the same vertical inset on
 * `ScreenTopInsetProvider`, so body content (ScreenHeading, etc.) sits at
 * the same y-position across header-visible and header-hidden screens.
 */
export function OnboardingHeader({ maxContentWidth, paddingTop = 0 }: OnboardingHeaderProps) {
  const { progress: target, canShowBack, canShowHeader, goBack } = useOnboardingSteps();
  const progressVisible = target !== null;

  // Progress fill is driven via scaleX on an Animated.Value so the timing
  // runs on the native driver (off the JS thread, smooth at 60fps even
  // when JS is busy). Initialized to 0 so the first visible step animates
  // in from empty — gives the user a visible "advancing" cue.
  const progressRef = useRef<Animated.Value | null>(null);
  if (progressRef.current === null) progressRef.current = new Animated.Value(0);
  const progress = progressRef.current;

  // Track the previous target so we only animate on actual target changes.
  // Same-target re-renders (navigating between sibling steps in the same
  // group, e.g. /company-search ↔ /employer-or-health-plan-verification)
  // leave the bar untouched.
  const previousTargetRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === null) {
      previousTargetRef.current = null;
      return;
    }
    if (previousTargetRef.current !== target) {
      Animated.timing(progress, {
        toValue: target,
        duration: ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start();
    }
    previousTargetRef.current = target;
  }, [target, progress]);

  // Step opted out of the chrome (e.g. /account-creation). FrostedSurface is
  // omitted entirely. The onboarding layout still publishes the same top
  // inset to ScreenTopInsetProvider so body content positions consistently.
  if (!canShowHeader) return null;

  return (
    <FrostedSurface className='absolute left-0 right-0 top-0'>
      <View
        // Reserve safe-area space (status bar on native, zero on web) above
        // the row. On web, also add visual breathing room with mt-8 since
        // there's no system bar to push us down. On native, the safe-area
        // inset alone is enough — stacking another margin would double up
        // against the inset.
        style={{ paddingTop }}
      >
        <View
          className={cn(
            'mx-auto h-12 w-full flex-row items-center gap-4 px-6',
            Platform.OS === 'web' ? 'mt-8' : ''
          )}
          style={{ maxWidth: maxContentWidth }}
        >
          {canShowBack && goBack ? (
            <Pressable
              onPress={goBack}
              testID='onboarding-back'
              accessibilityRole='button'
              accessibilityLabel='Go back'
              className='h-10 w-10 items-center justify-center rounded-full bg-background-50'
            >
              <ChevronLeftIcon size={24} className='text-typography-900' />
            </Pressable>
          ) : null}
          {progressVisible ? (
            // Outer track clips the scaled fill back to a rounded pill so the
            // right edge keeps its border-radius. The inner Animated.View is a
            // full-width bar transformed via scaleX with a left-anchored
            // origin — transform animations don't need a layout pass per frame.
            <View className='h-1.5 flex-1 overflow-hidden rounded-full bg-background-50 dark:bg-outline-100'>
              <Animated.View
                style={{
                  height: '100%',
                  width: '100%',
                  transformOrigin: 'left',
                  transform: [{ scaleX: progress }],
                }}
                className='bg-primary-500'
              />
            </View>
          ) : (
            // Empty spacer keeps the back button hugging the left edge so the
            // layout stays stable across screens that show vs. hide the bar.
            <View className='flex-1' />
          )}
        </View>
      </View>
    </FrostedSurface>
  );
}
